/**
 * LoginRoute (Plan 01-12, Task 3; to moduser i #141, styrt av enheten i #142).
 *
 * Ved oppstart spør vi GET /api/auth/device:
 *
 *   - innrullert kjøkkenskjerm → 'profiles': <ProfileLogin> (avatarrutenett +
 *     PIN fra /api/auth/roster), med lenken «Logg inn med brukernavn»
 *   - ellers → bare 'password': <PasswordLoginForm>, uten avatarer og uten
 *     lenke til dem — PIN virker ikke her uansett
 *
 * Et medlem uten PIN, eller lenken fra rutenettet, åpner passordskjemaet
 * (med brukernavnet utfylt når vi har det). Svarer rosteret ikke, er enheten
 * tilbakekalt i mellomtiden, og vi faller tilbake til bare passord.
 *
 * Erstatter DeviceClassModal (D-09): om enheten er en kjøkkenskjerm avgjøres
 * nå av innrulleringen, ikke av et spørsmål til den som står der.
 *
 * Ved vellykket innlogging tømmes cachen for forrige medlem
 * (useResetMemberCache) før navigate('/') — eller til /velg-passord når
 * medlemmet fortsatt har det arvede husstandspassordet (#143).
 *
 * Passordvisningen følger Stell_Innlogging (#159, steg 9g): merkevaretopp på
 * panelgradienten, «Logg inn», skjemaet, hjelpetekst om engangslenken og
 * nederst enten lenken tilbake til profilene (innrullert enhet) eller en
 * linje om at kjøkkenskjermen bruker PIN. Familiekoden (FF-09, #177) står
 * i passordskjemaet, unntatt på en innrullert kjøkkenskjerm, og
 * `?familie=<kode>` fyller den ut (også på aliaset /logg-inn). Profilvisningen (kjøkken-PIN)
 * følger Stell_KjokkenPIN (#160, steg 10) og eier sin egen luft.
 *
 * #162 (D6): «Hvem er du?» i hvilemodus navigerer hit med medlemmet i
 * ruteren sin state (LoginLocationState). Da åpnes PIN-skjermen rett på, og
 * Avbryt legger skjermen i hvile igjen og går til `/`. Raden vekker skjermen
 * idet den navigerer hit, så SwIdleReload ikke laster siden på nytt midt i
 * PIN-en. Medlemmet brukes én gang: state tømmes i historikken (tilbake-knappen
 * åpner ikke PIN-en igjen), og etter en tur til passordskjemaet viser
 * «Velg profil» rutenettet.
 */
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { LoginResponse } from '@stell/shared';
import { PANEL_SURFACE } from '../../components/primitives/DatePanel';
import { useResetMemberCache } from '../auth/sessionCache';
import { CHOOSE_PASSWORD_PATH, FAMILY_SETUP_PATH } from '../auth/ChooseOwnPasswordRoute';
import { AuthApi } from '../auth/authApi';
import { PasswordLoginForm } from './PasswordLoginForm';
import { ProfileLogin } from './ProfileLogin';
import { useDashboard } from '../dashboard/DashboardStore';

/** State fra «Hvem er du?» i hvilemodus (#162). */
export interface LoginLocationState {
  memberId: string;
}

function presetMemberId(state: unknown): string | undefined {
  const id = (state as Partial<LoginLocationState> | null)?.memberId;
  return typeof id === 'string' ? id : undefined;
}

export type LoginMode = 'password' | 'profiles';

type View =
  | { kind: 'loading' }
  | { kind: 'profiles' }
  | {
      kind: 'password';
      enrolled: boolean;
      username: string;
      /**
       * Linja «På kjøkkenskjermen logger du inn med PIN.» vises bare når
       * serveren har sagt at dette ikke er en kjøkkenskjerm. Ved nettverksfeil
       * eller en tilbakekalt kjøkkenskjerm vet vi ikke, og der virker ikke PIN.
       */
      pinHint: boolean;
    };

export function LoginRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const resetMemberCache = useResetMemberCache();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const linkedFamily = searchParams.get('familie')?.trim() ?? '';
  // Lest én gang ved montering; state tømmes rett under.
  const [fromIdle] = useState(() => presetMemberId(location.state) !== undefined);
  const [presetMember, setPresetMember] = useState(() => presetMemberId(location.state));
  const goIdle = useDashboard((s) => s.goIdle);
  useEffect(() => {
    if (fromIdle) void navigate('/login', { replace: true, state: null });
  }, [fromIdle, navigate]);
  const [view, setView] = useState<View>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    AuthApi.device().then(
      (status) => {
        if (cancelled) return;
        setView(
          status.enrolled
            ? { kind: 'profiles' }
            : { kind: 'password', enrolled: false, username: '', pinHint: true },
        );
      },
      // Uten svar kan vi ikke vite om PIN virker — passord virker alltid.
      () => {
        if (!cancelled) setView({ kind: 'password', enrolled: false, username: '', pinHint: false });
      },
    );
    return () => {
      cancelled = true;
    };
  }, []);

  const onLoggedIn = useCallback(
    (res: LoginResponse) => {
      resetMemberCache('logged_in');
      // `?.`: eldre svar (og testdobler) uten medlem sendes til / som før.
      navigate(
        res.member?.must_change_password
          ? CHOOSE_PASSWORD_PATH
          : res.member?.family_setup_pending
            ? FAMILY_SETUP_PATH
            : '/',
      );
    },
    [resetMemberCache, navigate],
  );

  const backToIdle = useCallback(() => {
    goIdle();
    void navigate('/', { replace: true });
  }, [goIdle, navigate]);

  const onUsePassword = useCallback((username?: string) => {
    setPresetMember(undefined);
    setView({ kind: 'password', enrolled: true, username: username ?? '', pinHint: false });
  }, []);

  const onUnavailable = useCallback(() => {
    setView({ kind: 'password', enrolled: false, username: '', pinHint: false });
  }, []);

  const mode: LoginMode | 'loading' = view.kind;

  return (
    <main
      data-route="login"
      data-login-mode={mode}
      className={
        view.kind === 'password'
          ? 'relative flex min-h-dvh flex-col overflow-hidden'
          : view.kind === 'profiles'
            ? 'relative flex min-h-dvh flex-col'
            : 'flex min-h-dvh flex-col items-center justify-start p-12'
      }
    >
      {view.kind === 'loading' ? (
        <p data-testid="login-device-loading" className="text-ink-2 text-body">
          {t('login.loading')}
        </p>
      ) : view.kind === 'profiles' ? (
        <ProfileLogin
          onSuccess={onLoggedIn}
          onUsePassword={onUsePassword}
          onUnavailable={onUnavailable}
          initialMemberId={presetMember}
          onCancel={fromIdle ? backToIdle : undefined}
        />
      ) : (
        <>
          {/* Panelbuen bak merket: bred ellipse som går ut over kantene. */}
          <div
            aria-hidden="true"
            className={`${PANEL_SURFACE} pointer-events-none absolute -top-30 -right-15 -left-15 h-105 panel-arc opacity-95`}
          />
          <div className="relative mx-auto flex w-full max-w-md grow flex-col gap-4 px-6 pt-30 pb-9">
            <div className="mb-26 text-on-panel">
              <div className="flex items-center gap-2.5">
                {/* Merket: to prikker der den mørke ligger over den grønne. */}
                <span aria-hidden="true" className="relative h-4.5 w-8 shrink-0">
                  <span className="absolute top-0 left-3.5 h-4.5 w-4.5 rounded-full bg-panel-accent" />
                  <span className="absolute top-0 left-0 h-4.5 w-4.5 rounded-full bg-on-panel" />
                </span>
                <span className="text-brand font-extrabold">{t('dashboard.header.wordmark')}</span>
              </div>
              <p className="mt-1.5 mb-0 text-base font-semibold">{t('login.brand_tagline')}</p>
            </div>

            <h1 className="m-0 text-heading-m font-extrabold text-ink">{t('login.password.title')}</h1>
            <PasswordLoginForm
              key={view.username}
              onSuccess={onLoggedIn}
              initialUsername={view.username}
              showFamily={!view.enrolled}
              initialFamily={linkedFamily}
            />
            <p className="m-0 text-center text-control leading-snug text-balance text-ink-2">
              {t('login.password.forgot_hint')}
            </p>

            <div className="mt-auto pt-6 text-center">
              {view.enrolled ? (
                <button
                  data-testid="login-use-profiles"
                  type="button"
                  className="min-h-11 px-3 text-sm font-bold text-ink underline"
                  onClick={() => setView({ kind: 'profiles' })}
                >
                  {t('login.password.use_profiles')}
                </button>
              ) : view.pinHint ? (
                <p className="m-0 text-control text-ink-2">{t('login.password.kitchen_pin_hint')}</p>
              ) : null}
            </div>
          </div>
        </>
      )}
    </main>
  );
}
